"use client"

import { Button, useRetryFitAnalysis, getHrefWithThreadPreservation } from '@yeshuman/sdk'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { SmartAnalysisResultsShell } from '../search/smart-analysis-results-shell'
import { useJobWorkspaceCandidatePanel } from '../../hooks/use-job-workspace-candidate-panel'
import { useJobProspectMatches } from '../../hooks/use-job-prospect-matches'
import { useJobAnalysisCoverage } from '../../hooks/use-job-analysis-coverage'
import { useEmployerJobTalent } from '../../hooks/use-employer-job-talent'
import { useApplicationNudge } from '../../hooks/use-application-nudge'
import { AnalysisRetryButton } from '../search/analysis-retry-button'
import { ReportEmailDialog } from './report-email-dialog'
import { ShareReportMenu } from './share-report-menu'
import { useJobWorkspaceScope } from './job-workspace-scope'
import type { ApplicantProfileSummary } from '../../hooks/use-opportunity-applicants'
import {
  buildJobWorkspaceApplicantsPath,
  buildJobWorkspaceSuggestionsPath,
} from '../../lib/job-workspace-person-paths'

export type EmployerTalentPanelProps = {
  opportunityId: string
  opportunityTitle?: string
}

export function EmployerTalentPanel({
  opportunityId,
  opportunityTitle,
}: EmployerTalentPanelProps) {
  const navigate = useNavigate()
  const scope = useJobWorkspaceScope()
  const [reportOpen, setReportOpen] = useState(false)
  const candidatePanel = useJobWorkspaceCandidatePanel(opportunityId)
  const {
    board,
    activeApplicants,
    emptyActiveApplicants,
    rankedCandidates,
    cohortSummary,
    cohortSummaryAt,
    isLoading,
    errorMessage,
  } = useEmployerJobTalent(opportunityId)
  const {
    retryEmployer,
    retryEmployerList,
    mutatingApplicationId,
    pendingApplicationIds,
    stuckApplicationIds,
    isListPending,
  } = useRetryFitAnalysis()

  const { data: prospectData } = useJobProspectMatches(opportunityId)
  const { data: coverage } = useJobAnalysisCoverage(opportunityId)
  const nudgeMutation = useApplicationNudge(opportunityId)

  const applicantProfileById = useMemo(() => {
    const record: Record<string, ApplicantProfileSummary> = {}
    for (const applicant of board?.applicants ?? []) {
      record[applicant.profile.id] = applicant.profile
    }
    return record
  }, [board?.applicants])

  const goToPipeline = () => {
    navigate(getHrefWithThreadPreservation(buildJobWorkspaceApplicantsPath(opportunityId)))
  }

  const goToInvite = () => {
    navigate(getHrefWithThreadPreservation(buildJobWorkspaceSuggestionsPath(opportunityId)))
  }

  const viewInPipeline = (profileId: string) => {
    const applicant = (board?.applicants ?? []).find((entry) => entry.profile.id === profileId)
    if (applicant) {
      navigate(
        getHrefWithThreadPreservation(
          `${buildJobWorkspaceApplicantsPath(opportunityId)}/${applicant.id}`,
        ),
      )
      return
    }
    goToPipeline()
  }

  const rerunApplicantIds = useMemo(
    () => activeApplicants.filter((row) => !pendingApplicationIds.has(row.id)).map((row) => row.id),
    [activeApplicants, pendingApplicationIds],
  )

  const displayResults =
    rankedCandidates.length > 0
      ? {
          session_id: `talent-${opportunityId}`,
          total_candidates_evaluated: rankedCandidates.length,
          llm_judged_count: 0,
          top_matches: rankedCandidates.map((c) => c.full!).filter(Boolean),
          search_summary: cohortSummary || undefined,
        }
      : null

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <ShareReportMenu kind="job" subjectId={opportunityId} />
        <Button type="button" variant="outline" size="sm" onClick={() => setReportOpen(true)}>
          Email report
        </Button>
        {activeApplicants.length > 0 ? (
          <AnalysisRetryButton
            mode="run"
            ariaLabel="Re-run analysis for your applicants"
            disabled={rerunApplicantIds.length === 0}
            pending={isListPending}
            onClick={() => retryEmployerList(opportunityId, rerunApplicantIds)}
          />
        ) : null}
      </div>
      <ReportEmailDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        kind="job"
        subjectId={opportunityId}
        asRecruiter={scope.mode === 'recruiter'}
      />
      <SmartAnalysisResultsShell
        direction="candidates"
        layoutVariant="job-report"
        liveItems={rankedCandidates}
        results={displayResults}
        stages={[]}
        isSearching={false}
        isHydrating={isLoading}
        searchError={errorMessage ? new Error(errorMessage) : null}
        introJobTitle={opportunityTitle}
        introJobScoped
        introEmptyActiveApplicants={emptyActiveApplicants}
        analysisLastUpdatedAt={cohortSummaryAt}
        jobReportLiveComplete={rankedCandidates.length > 0}
        searchSummary={cohortSummary || null}
        onOpenPerson={(profileId, label, evaluationId) =>
          candidatePanel.openByProfileId(profileId, label, {
            evaluationId,
            origin: 'talent' as const,
          })
        }
        applicantProfileById={applicantProfileById}
        jobReport={{
          applicants: activeApplicants,
          prospectMatches: prospectData?.matches ?? [],
          coverage: coverage ?? null,
          onReviewPipeline: goToPipeline,
          onReviewInvite: goToInvite,
          onViewInPipeline: viewInPipeline,
          onNudge: (applicationId) => nudgeMutation.mutate(applicationId),
          nudgePending: nudgeMutation.isPending,
          onRetryApplicant: (applicationId) => retryEmployer(applicationId, opportunityId),
          retryPendingApplicationId: mutatingApplicationId,
          pendingApplicationIds,
          stuckApplicationIds,
        }}
      />
    </div>
  )
}

/** @deprecated Use EmployerTalentPanel */
export const EmployerSmartSearchPanel = EmployerTalentPanel
